import { palette } from '../palette';
import { BLUE_PRIMARY, DARK_0, DARK_3, DARK_4, DARK_5, DARK_7, LIGHT_0, LIGHT_5, LIGHT_BACKGROUND } from './tokens';

import type { PaletteMode, PaletteOptions } from '@mui/material';

/**
 * The dark half of the Design System 2.0 palette (HNS-263).
 *
 * The legacy palette barely distinguishes the two modes — it flips `text.primary` to white and stops
 * there, leaving `background.paper` at #fff and `divider` at `grey[200]`. That was survivable while
 * the only dark surfaces were action bars holding white icons, but a dark PANEL needs the mode to
 * mean something: its children read `background.paper`, `divider` and `text.secondary` off the theme
 * and would otherwise render a light card inside a dark frame.
 *
 * The ramp below is `Color/Dark/*` from `design-system.css`. Only the panel background (`Dark/3`) is
 * specified by design; the rest is EXTRAPOLATED one step at a time up the ramp — nested surfaces at
 * `Dark/4`, borders at `Dark/5` — and wants a designer's confirmation.
 */
const darkPalette = (base: Partial<PaletteOptions>): Partial<PaletteOptions> => ({
  ...base,
  background: { default: DARK_0, paper: DARK_3 },
  text: { primary: LIGHT_0, secondary: LIGHT_5, disabled: DARK_7 },
  divider: DARK_5,
  action: { active: LIGHT_0, disabled: DARK_7, disabledBackground: DARK_4 },
  // Mirrors the DS 2.0 secondary button on dark (`.dark .ds-btn-secondary-default`): a `Dark/4` fill
  // that lifts to `Dark/5` on hover.
  secondary: { main: DARK_4, dark: DARK_5, contrastText: LIGHT_0 },
});

/**
 * The Design System 2.0 palette (HNS-259).
 *
 * Two tokens move; the rest of the ramps are still the legacy ones and remain the work outstanding
 * here.
 *
 *   - `primary.main` — `Color/Blue/Primary`, the accent DS 2.0 draws every emphasised control with,
 *     replacing `blue[600]` (#1570ef). This is the widest-reaching line in the refresh: MUI derives
 *     `light`, `dark` and `contrastText` from it, and roughly 76 files read it for links, icons,
 *     selected states, focus rings, progress bars and switches. Setting it here rather than
 *     repainting each control is the point — a component override per surface would leave the app
 *     two shades of blue for as long as any surface was missed.
 *   - `background.default` — from `grey[25]` (#fcfcfd, near enough white to read as white) to
 *     `Color/Light/Background` (#f9fafa), which is what every DS 2.0 screen sits on. Figma node
 *     18187:17351.
 *
 * `primary` is still honoured when a caller passes one, so the per-account branding seam the
 * signature implies survives the refresh — though nothing in the app passes it today.
 *
 * `background.paper` deliberately stays white in light mode: the Figma card is white ON the tinted
 * page, and that contrast is the whole point of the token.
 */
export const refreshPalette = (primary?: string, mode: PaletteMode = 'light'): Partial<PaletteOptions> => {
  const base = { ...palette(primary, mode), primary: { main: primary ?? BLUE_PRIMARY } };

  if (mode === 'dark') return darkPalette(base);

  return {
    ...base,
    background: { ...base.background, default: LIGHT_BACKGROUND },
  };
};
